import { apiClient } from '@/services/apiClient';

/**
 * Per-provider ads dashboards (Meta Ads, Google Ads): one report for a date
 * range, optionally narrowed to some campaigns.
 *
 * `campaigns` always lists every campaign with activity in the range — it is
 * also what the campaign filter offers. `totals`, `previousTotals`, `daily`
 * and `conversionsByType` follow the filter.
 */
export type AdsProvider = 'meta' | 'google';

export interface AdsMetrics {
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversionValue: number;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  cpa: number | null;
  roas: number | null;
}

export interface AdsDailyRow extends AdsMetrics {
  date: string;
}

export interface AdsCampaignRow extends AdsMetrics {
  id: string;
  name: string;
  /** active | enabled | paused | removed | unknown | provider-specific */
  status: string;
  /** Meta objective or Google channel type, humanized ("Leads", "Search"). */
  type: string | null;
}

export interface AdsConversionType {
  label: string;
  count: number;
  value: number;
}

export interface AdsReport {
  provider: 'meta_ads' | 'google_ads';
  connected: boolean;
  error: string | null;
  currency: string | null;
  startDate: string;
  endDate: string;
  previous: { startDate: string; endDate: string };
  campaignIds: string[];
  totals?: AdsMetrics;
  previousTotals?: AdsMetrics | null;
  daily?: AdsDailyRow[];
  campaigns?: AdsCampaignRow[];
  conversionsByType?: AdsConversionType[];
}

export interface AdsReportQuery {
  start: string;
  end: string;
  campaignIds: string[];
}

export function getAdsReport(provider: AdsProvider, q: AdsReportQuery): Promise<AdsReport> {
  return apiClient
    .get(`/marketing/ads/${provider}/report`, {
      params: { start: q.start, end: q.end, campaignIds: q.campaignIds.length ? q.campaignIds : undefined },
    })
    .then((r) => r.data.data);
}
